/**
 * Fetches and renders a paginated collection of bounties or submissions as
 * ItemCards, in "grid" (with previous/next pager) or horizontal "row" format.
 * The discriminated props (itemType + type: browse / user-listings /
 * user-pursuits / user-submissions / bounty-submissions) select which API is
 * called (BountyAPI, UserBountyAPI, UserSubmissionAPI, UserBountySubmissionAPI)
 * filtered by statusType and by category/categoryType/sex query params; clicking
 * a card navigates to the matching detail route. Errors surface via ErrorContext.
 * Used by BrowseCollection and the account Listings/Pursuits/Submissions/
 * BountySubmissions pages.
 */
import React, { useState, useEffect } from "react";
import { Bounty, BountyStatus } from "@/types/bounty";
import { SubmissionWithBounty, SubmissionStatus } from "@/types/submission";
import ItemCard from "./ItemCard";
import { PaginatedResponse, Pagination } from "@/types/pagination";
import { UserBountyAPI } from "@/api/userBounty";
import { UserBountySubmissionAPI } from "@/api/userBountySubmission";
import { useUserState } from "@/contexts/UserContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BountyAPI } from "@/api/bounty";
import { Category, Sex } from "@/types/category";
import LoadingSpinner from "@/components/LoadingSpinner";
import { formatCategorySexCategoryType } from "@/utils/bounty";
import UserSubmissionAPI from "@/api/userSubmission";
import { useError } from "@/contexts/ErrorContext";

type CollectionItem = Bounty | SubmissionWithBounty;
type FormatType = "grid" | "row";

type BountyCollectionType = "browse" | "user-listings" | "user-pursuits";
type SubmissionCollectionType = "bounty-submissions" | "user-submissions";
const isBounty = (item: CollectionItem): item is Bounty => "category" in item;

type BountyCollectionProps = {
    type: BountyCollectionType;
    format: FormatType;
    itemType: "bounty";
    statusType: BountyStatus;
    emptyStateText?: string;
};

type SubmissionCollectionProps = {
    type: SubmissionCollectionType;
    format: FormatType;
    itemType: "submission";
    statusType: SubmissionStatus;
    emptyStateText?: string;
};

type ItemCollectionProps = BountyCollectionProps | SubmissionCollectionProps;

const ItemCollection: React.FC<ItemCollectionProps> = (props) => {
    const { type, format, itemType, statusType, emptyStateText: customEmptyStateText } = props;

    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const userState = useUserState();
    const { showError } = useError();
    const [items, setItems] = useState<CollectionItem[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [loading, setLoading] = useState(true);

    const bountyId =
        type === "bounty-submissions"
            ? parseInt(params.bountyId || location.pathname.split("/").pop() || "0")
            : undefined;

    const queryParams = new URLSearchParams(location.search);
    const category: Category | null = (queryParams.get("category") as Category) || null;
    const categoryType: string | null = queryParams.get("categoryType") || null;
    const sex: Sex | null = (queryParams.get("sex") as Sex) || null;

    useEffect(() => {
        fetchItems(1);
    }, [category, categoryType, sex, type, itemType, bountyId]);

    function onItemClick(item: CollectionItem) {
        const mainContent = document.querySelector(".overflow-auto");
        if (mainContent) {
            mainContent.scrollTop = 0;
        }

        if (isBounty(item)) {
            const state = { bounty: item };

            if (type === "user-listings" && statusType === "completed") {
                navigate(`/account/listings/${item.id}/submissions/accepted`, {
                    state,
                });
            } else if (type === "user-listings") {
                navigate(`/account/listings/${item.id}`, { state });
            } else if (type === "user-pursuits") {
                navigate(`/account/pursuits/${item.id}`, { state });
            } else if (type === "browse") {
                navigate(`/bounties/${item.id}`, { state });
            } else {
                throw new Error("Invalid bounty collection type");
            }
        } else {
            // It's a submission
            const state = { submission: item };

            if (type === "user-submissions") {
                navigate(`/account/submissions/${item.id}`, { state });
            } else if (type === "bounty-submissions") {
                navigate(`/account/listings/${bountyId!}/submissions/${item.id}`, { state });
            } else {
                throw new Error("Invalid submission collection type");
            }
        }
    }

    const fetchItems = async (page: number = 1) => {
        setLoading(true);
        try {
            if (itemType === "bounty") {
                if (type === "user-listings") {
                    const response: PaginatedResponse<Bounty> = await UserBountyAPI.fetchBounties(
                        userState!.id,
                        {
                            page,
                            perPage: 12,
                            category,
                            categoryType,
                            sex,
                            status: statusType,
                        }
                    );
                    setItems(response.bounties);
                    setPagination(response.pagination);
                } else if (type === "user-pursuits") {
                    const response: PaginatedResponse<Bounty> = await UserBountyAPI.fetchPursuits(
                        userState!.id,
                        {
                            page,
                            perPage: 12,
                            category,
                            categoryType,
                            sex,
                            status: statusType,
                        }
                    );
                    setItems(response.bounties);
                    setPagination(response.pagination);
                } else if (type === "browse") {
                    const response: PaginatedResponse<Bounty> = await BountyAPI.fetchBounties({
                        page,
                        perPage: 12,
                        category,
                        categoryType,
                        sex,
                        status: statusType,
                    });
                    setItems(response.bounties);
                    setPagination(response.pagination);
                } else {
                    throw new Error("Invalid bounty collection type");
                }
            } else if (itemType === "submission") {
                if (type === "user-submissions") {
                    const response: PaginatedResponse<SubmissionWithBounty> =
                        await UserSubmissionAPI.fetchUserSubmissions(userState!.id, {
                            page,
                            perPage: 12,
                            status: statusType,
                        });
                    setItems(response.submissions);
                    setPagination(response.pagination);
                } else if (type === "bounty-submissions") {
                    const response: PaginatedResponse<SubmissionWithBounty> =
                        await UserBountySubmissionAPI.fetchUserBountySubmissions(
                            userState!.id,
                            bountyId!,
                            {
                                page,
                                perPage: 12,
                                status: statusType,
                            }
                        );
                    setItems(response.submissions);
                    setPagination(response.pagination);
                } else {
                    throw new Error("Invalid submission collection type");
                }
            }
        } catch (error) {
            console.error(`Failed to fetch ${itemType}s:`, error);
            showError(`failed to fetch ${pluralizeItemType(itemType)}`);
        } finally {
            setLoading(false);
        }
    };

    const pluralizeItemType = (itemType: string) => {
        if (itemType === "bounty") {
            return "bounties";
        } else if (itemType === "submission") {
            return "submissions";
        }
    };

    const handlePageChange = (newPage: number) => {
        fetchItems(newPage);
    };

    const getEmptyStateText = () => {
        if (customEmptyStateText) return customEmptyStateText;

        if (itemType === "bounty") {
            if (type === "user-listings") {
                return "no active listings found";
            } else if (type === "user-pursuits") {
                return "no active pursuits found";
            } else if (type === "browse") {
                return `no bounties found for\n${formatCategorySexCategoryType(
                    category,
                    sex,
                    categoryType
                )}`;
            } else {
                throw new Error("Invalid item collection type");
            }
        } else if (itemType === "submission") {
            if (type === "user-submissions") {
                return "no submissions found";
            } else if (type === "bounty-submissions") {
                return "no submissions yet";
            } else {
                throw new Error("Invalid item collection type");
            }
        } else {
            throw new Error("Invalid item collection type");
        }
    };

    const getContainerClasses = () => {
        if (format === "grid") {
            return "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 p-8 overflow-y-auto";
        } else if (format === "row") {
            return "flex flex-row flex-nowrap gap-4 pt-2 pb-6 px-6 overflow-x-auto hide-scrollbar";
        } else {
            throw new Error("Invalid format type");
        }
    };

    return (
        <div
            className={`flex flex-col w-full gap-8 font-porsui ${
                format === "grid" ? "h-full" : ""
            }`}
        >
            {loading ? (
                <div className="flex w-full h-full items-center justify-center bg-transparent z-50">
                    <LoadingSpinner />
                </div>
            ) : items.length === 0 ? (
                <div
                    className={`flex flex-col items-center justify-center w-full py-16 ${
                        format === "grid" ? "h-full" : ""
                    }`}
                >
                    <p className="text-center text-slate-400 text-lg font-porsui whitespace-pre-line mb-4">
                        {getEmptyStateText()}
                    </p>
                </div>
            ) : (
                <>
                    <div className={getContainerClasses()}>
                        {items.map((item) => (
                            <ItemCard
                                key={item.id}
                                item={item}
                                onClick={() => onItemClick(item)}
                                className={format === "row" ? "flex-shrink-0 w-56" : ""}
                            />
                        ))}
                    </div>

                    {format === "grid" && (pagination?.pageCount ?? 1) > 1 && (
                        <div className="flex justify-center items-center gap-3 mt-auto pb-6">
                            <button
                                onClick={() => handlePageChange((pagination?.currentPage ?? 1) - 1)}
                                disabled={!pagination?.hasPrev}
                                className="px-5 py-2 bg-white rounded-full shadow-sm border border-slate-100 
                                    hover:shadow-md hover:border-slate-200 
                                    disabled:opacity-40 disabled:hover:shadow-sm disabled:hover:border-slate-100
                                    font-porsui text-sm text-slate-700 transition-all duration-200"
                            >
                                previous
                            </button>
                            <span className="px-4 py-2 font-porsui text-xs text-slate-400">
                                {pagination?.currentPage ?? 1} / {pagination?.pageCount ?? 1}
                            </span>
                            <button
                                onClick={() => handlePageChange((pagination?.currentPage ?? 1) + 1)}
                                disabled={!pagination?.hasNext}
                                className="px-5 py-2 bg-white rounded-full shadow-sm border border-slate-100 
                                    hover:shadow-md hover:border-slate-200 
                                    disabled:opacity-40 disabled:hover:shadow-sm disabled:hover:border-slate-100
                                    font-porsui text-sm text-slate-700 transition-all duration-200"
                            >
                                next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ItemCollection;
